import type { TemplateResult } from 'lit-html'

import type { App } from './app.ts'
import type { Route, ScreenId } from './route.ts'
import { createStore, type Store } from './store.ts'

/**
 * A screen: one small store and one `render(state)` (`docs/plan.md` M5 task 1).
 *
 * `enter` is called when the route arrives at this screen and is the only place
 * a screen talks to the API for its own data; `render` is a pure function of
 * what the store holds. Nothing about the game is decided in either: a screen
 * sends a direction or an id and shows what comes back.
 *
 * A request cannot be called back. Every `enter` is therefore given a way to
 * ask whether it is still the load the screen is waiting for, and drops its
 * answer if it is not (#115).
 */
export type Screen = {
  readonly id: ScreenId
  /** Re-render when the screen's own state changes. */
  watch(listener: () => void): void
  /**
   * Forget everything this screen was holding.
   *
   * A store lives as long as the module, which is what lets a screen keep its
   * place between visits -- and means the next player to sign in would inherit
   * the last one's, guidance and all (#95).
   */
  reset(): void
  enter(app: App, route: Route): void | Promise<void>
  render(app: App): TemplateResult
  /** Keys the screen answers to while it is the one on show. */
  keydown(event: KeyboardEvent, app: App): void
}

/**
 * Whether the load that asked is still the one the screen is waiting for.
 *
 * False once the route has moved on or the player has changed, which is the
 * whole of what "no longer mine" means here. An `enter` calls it after every
 * await and before every write, so a response belonging to somebody who has
 * left cannot land in the screen the next person is looking at (#115).
 */
export type StillCurrent = () => boolean

export type ScreenDefinition<S extends object> = {
  readonly id: ScreenId
  readonly initial: S
  /** Load what the screen shows. Called on every arrival at its route. */
  enter(app: App, route: Route, store: Store<S>, current: StillCurrent): void | Promise<void>
  render(state: S, app: App, store: Store<S>): TemplateResult
  keydown?(event: KeyboardEvent, state: S, app: App, store: Store<S>): void
}

export function defineScreen<S extends object>(definition: ScreenDefinition<S>): Screen {
  const store = createStore(definition.initial)
  // Which load is the current one. Bumped by a fresh arrival and by a reset,
  // because both mean the answers in flight are answers to an old question.
  let generation = 0
  return {
    id: definition.id,
    watch: (listener) => {
      store.watch(listener)
    },
    reset: () => {
      generation += 1
      store.reset()
    },
    enter: (app, route) => {
      generation += 1
      const mine = generation
      return definition.enter(app, route, store, () => generation === mine)
    },
    render: (app) => definition.render(store.get(), app, store),
    keydown: (event, app) => definition.keydown?.(event, store.get(), app, store),
  }
}
